/**
 * Validation utilities for financial data
 */

/**
 * validateCell, validateIncomeStatement and a private calculateFormula used
 * to live here. calculateFormula was a fourth copy of the income-statement
 * formula engine and by far the most dangerous one: it called raw `eval()` on
 * a string built from a user-supplied formula with **no allowlist check at
 * all**, unlike the other copies. It was unreachable — its only caller,
 * validateIncomeStatement, was itself unreferenced — but it sat one import
 * away from being live inside a module that is still imported.
 *
 * All three are deleted. Calculated rows are computed server-side by
 * backend/services/incomeStatementCalculator.js, so a client-side
 * recalculation to cross-check against is not something we want back.
 * See docs/testing/bug-register.md, DT-047 and DT-048.
 */

/**
 * Validate import file structure
 */
export const validateImportFile = (
	data: any[][],
	expectedQuarters: number = 5
): {
	isValid: boolean
	errors: string[]
	warnings: string[]
} => {
	console.log('=== VALIDATING IMPORT FILE ===')
	console.log('Data length:', data?.length)
	console.log('Expected quarters:', expectedQuarters)
	
	const errors: string[] = []
	const warnings: string[] = []

	// Check if data exists
	if (!data || data.length === 0) {
		console.error('File is empty or null')
		errors.push('File is empty or could not be parsed')
		return { isValid: false, errors, warnings }
	}

	// Check minimum rows (header + at least 1 data row)
	if (data.length < 2) {
		errors.push('File must contain at least a header row and one data row')
		return { isValid: false, errors, warnings }
	}

	// Check header row
	const headers = data[0]
	if (!headers || headers.length < 2) {
		errors.push('Invalid header row. Expected: Line Item, Q1, Q2, ...')
		return { isValid: false, errors, warnings }
	}

	// Check first column is text (line item labels)
	if (!headers[0] || typeof headers[0] !== 'string') {
		errors.push('First column must be "Line Item" or similar label')
	}

	// Check for quarter columns
	const quarterColumns = headers.length - 1
	if (quarterColumns < 1) {
		errors.push('No quarter columns found. File must have at least one quarter of data.')
		return { isValid: false, errors, warnings }
	}

	if (quarterColumns !== expectedQuarters) {
		warnings.push(
			`Expected ${expectedQuarters} quarters, found ${quarterColumns}. Data will be adjusted accordingly.`
		)
	}

	// Check data rows
	const dataRows = data.slice(1)
	dataRows.forEach((row, index) => {
		if (!row[0] || typeof row[0] !== 'string') {
			warnings.push(`Row ${index + 2}: Missing or invalid line item label`)
		}

		// Check for numeric values in quarter columns
		for (let i = 1; i < row.length; i++) {
			const value = row[i]
			if (value !== null && value !== undefined && value !== '') {
				const num = typeof value === 'number' ? value : parseFloat(value)
				if (isNaN(num)) {
					warnings.push(`Row ${index + 2}, Column ${i + 1}: Value "${value}" is not a valid number`)
				}
			}
		}
	})

	return {
		isValid: errors.length === 0,
		errors,
		warnings,
	}
}

